import React, { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { useCollaborations } from '../context/CollaborationsContext';
import CollaborationCard from './CollaborationCard';
import { Card } from './ui/card';
import { cn } from '../lib/utils';

const COL_CLASS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

export default function CollaborationGrid({ columns }) {
  const {
    filteredCollaborations,
    collaborations,
    reorderCollaborations,
    searchQuery,
    filter,
  } = useCollaborations();
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const dragNode = useRef(null);

  const cols = COL_CLASS[columns] || COL_CLASS[4];

  if (filteredCollaborations.length === 0) {
    return (
      <Card className="border-dashed">
        <div className="flex flex-col items-center justify-center text-center py-16 px-6">
          <div className="h-12 w-12 rounded-full bg-muted text-muted-foreground flex items-center justify-center mb-4">
            <Users className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold">
            {searchQuery || filter
              ? 'No se encontraron colaboraciones'
              : 'Aún no hay colaboraciones'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            {searchQuery
              ? `No hay resultados para "${searchQuery}"`
              : filter
              ? 'Ninguna colaboración coincide con el filtro activo'
              : 'Crea tu primera colaboración con el botón superior'}
          </p>
        </div>
      </Card>
    );
  }

  function handleDragStart(e, id) {
    setDragId(id);
    dragNode.current = e.currentTarget;
    setTimeout(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4';
    }, 0);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnter(e, id) {
    e.preventDefault();
    if (id !== dragId) setOverId(id);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e, targetId) {
    e.preventDefault();
    if (dragId === targetId) return;
    const allIds = collaborations.map((c) => c.id);
    const dragIdx = allIds.indexOf(dragId);
    const targetIdx = allIds.indexOf(targetId);
    if (dragIdx === -1 || targetIdx === -1) return;
    const newOrder = [...collaborations];
    const [removed] = newOrder.splice(dragIdx, 1);
    newOrder.splice(targetIdx, 0, removed);
    reorderCollaborations(newOrder);
    setDragId(null);
    setOverId(null);
  }

  function handleDragEnd() {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    dragNode.current = null;
    setDragId(null);
    setOverId(null);
  }

  return (
    <div className={cn('grid gap-4', cols)}>
      <AnimatePresence mode="popLayout">
        {filteredCollaborations.map((collab) => {
          const isOver = overId === collab.id && dragId !== collab.id;
          return (
            <motion.div
              key={collab.id}
              layout
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{
                opacity: { duration: 0.22 },
                scale: { duration: 0.22 },
                layout: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
              }}
              draggable
              onDragStart={(e) => handleDragStart(e, collab.id)}
              onDragEnter={(e) => handleDragEnter(e, collab.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, collab.id)}
              onDragEnd={handleDragEnd}
              className={cn(
                'rounded-lg cursor-grab min-w-0',
                isOver && 'outline outline-2 outline-dashed outline-primary bg-accent/40',
              )}
            >
              <CollaborationCard collab={collab} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
